import { useEffect, useState, useMemo } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { FiTrendingUp, FiFileText, FiLayers, FiAlertTriangle, FiDownload } from "react-icons/fi";
import { LuEye } from "react-icons/lu";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import UserAvatar from "../../../components/UserAvatar";
import NewTableComponent from "../../../components/NewTableComponent";
import { apiService } from "../../../services/apiService";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import { errorNotify, successNotify } from "../../../utils/toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerDropdown, fetchCostCenterDropdown } from "../../../store/Slices/dropdownSlice";

import "../../../styles/Access.css";
import "../../../styles/Button.css";
import "../../../styles/FinancialReports.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";

const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));



const REPORT_TYPE_META = {
  pnl: {
    key: "pnl",
    title: "Profit & Loss Report",
    chipLabel: "P&L",
    badgeClass: "is_trip_pnl",
    icon: FiTrendingUp,
  },
  ar: {
    key: "ar",
    title: "Accounts Receivable Report",
    chipLabel: "AR",
    badgeClass: "is_vehicle_pnl",
    icon: FiFileText,
  },
  ap: {
    key: "ap",
    title: "Accounts Payable Report",
    chipLabel: "AP",
    badgeClass: "is_trip_expense",
    icon: FiLayers,
  },
};

const renderFinancialReportTypeChip = (row = {}) => {
  const reportTypeMeta = REPORT_TYPE_META[row?.reportTypeKey] || REPORT_TYPE_META.pnl;
  const ReportTypeIcon = reportTypeMeta?.icon;
  const chipLabel = reportTypeMeta?.chipLabel || "-";
  const chipClassName = reportTypeMeta?.badgeClass || "is_default";

  return (
    <span className={`financial_report_type_chip ${chipClassName}`}>
      <span className="financial_report_type_chip_icon">
        {typeof ReportTypeIcon === "function" ? <ReportTypeIcon /> : ReportTypeIcon}
      </span>
      <span>{chipLabel}</span>
    </span>
  );
};

// Custom inline SVG icons matching the design
const BanknoteIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="20" height="14" rx="2" fill="#22C55E" fillOpacity="0.2" stroke="#22C55E" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" stroke="#22C55E" strokeWidth="2" />
    <path d="M6 9H7M17 15H18" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CostsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ProfitIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 6H23V12" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MarginIcon = () => (
  <span style={{ fontSize: "24px", fontWeight: "700", color: "#64748B", fontFamily: "monospace" }}>%</span>
);

const generateDummyReportData = (type, startDateVal, endDateVal, customerVal, customerLabel) => {
  const generatedTime = moment().format("YYYY-MM-DD HH:mm:ss");
  const formattedRange = `Range: ${startDateVal || "2026-06-01"} to ${endDateVal || "2026-06-30"}`;

  if (type === "pnl") {
    return {
      type: "pnl",
      typeLabel: "P&L",
      title: "Profit & Loss Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "PAID REVENUE", amount: "PKR 65,850.00", subtitle: "Cash basis", color: "green", icon: <BanknoteIcon /> },
        { label: "DIRECT COSTS", amount: "PKR 382,800.00", subtitle: "Fuel+Toll+Maint+Salary", color: "red", icon: <CostsIcon /> },
        { label: "NET PROFIT", amount: "PKR -316,950.00", subtitle: "Revenue - costs", color: "blue", icon: <ProfitIcon /> },
        { label: "MARGIN", amount: "-481.3%", subtitle: "Operating margin", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "ICL", message: "ICL: This customer has costs in the selected period but no paid revenue. Unpaid invoices may still be sitting in AR." },
        { customer: "T&T", message: "T&T: This customer has costs in the selected period but no paid revenue. Unpaid invoices may still be sitting in AR." }
      ],
      tableTitle: "Customer-Wise Revenue & Contribution (cash basis)",
      tableColumns: [
        { name: "CUSTOMER", selector: (row) => row.customer, minWidth: "150px" },
        { name: "PAID REVENUE", selector: (row) => `PKR ${row.paidRevenue.toLocaleString()}`, minWidth: "150px" },
        { name: "FUEL", selector: (row) => `PKR ${row.fuel.toLocaleString()}`, minWidth: "150px" },
        { name: "TOLLS", selector: (row) => `PKR ${row.tolls.toLocaleString()}`, minWidth: "120px" },
        { name: "MAINTENANCE", selector: (row) => `PKR ${row.maintenance.toLocaleString()}`, minWidth: "150px" },
        {
          name: "CONTRIBUTION", selector: (row) => (
            <span style={{ color: row.contribution < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
              PKR {row.contribution.toLocaleString()}
            </span>
          ), minWidth: "150px"
        },
        {
          name: "MARGIN", selector: (row) => (
            <span style={{ color: row.marginVal < 0 ? "#B42318" : "#027A48", fontWeight: 600 }}>
              {row.margin}
            </span>
          ), minWidth: "100px"
        }
      ],
      tableData: [
        { customer: "Colgate", paidRevenue: 50850, fuel: 106500, tolls: 7300, maintenance: 22000, contribution: -84950, marginVal: -167.1, margin: "-167.1%" },
        { customer: "ICL", paidRevenue: 0, fuel: 76000, tolls: 5000, maintenance: 8000, contribution: -89900, marginVal: 0, margin: "—" },
        { customer: "Hamza Mughal", paidRevenue: 15000, fuel: 125000, tolls: 9000, maintenance: 18000, contribution: -137000, marginVal: -913.3, margin: "-913.3%" },
        { customer: "T&T", paidRevenue: 0, fuel: 5500, tolls: 500, maintenance: 0, contribution: -6000, marginVal: 0, margin: "—" }
      ],
      tableNote: "Paid Revenue comes from recorded payments only. Unpaid invoice amounts are not counted as revenue. Driver salaries are company-wide and shown in the summary below, not split per customer.",
      companySummary: {
        title: "Company-Wide Summary",
        rows: [
          { label: "Total Paid Revenue", value: "PKR 65,850.00", isGreen: true },
          { label: "- Total Costs (Fuel + Toll + Maintenance + Salaries)", value: "PKR 382,800.00" },
          { label: "• Driver Salaries (locked payroll)", value: "PKR 0.00", isIndented: true },
          { label: "Net Profit", value: "PKR -316,950.00", isBlue: true }
        ],
        note: "Revenue is recognized when payment is recorded. Driver salaries are company-wide (not split per customer) and only counted from Locked payroll runs."
      }
    };
  } else if (type === "ar") {
    return {
      type: "ar",
      typeLabel: "AR",
      title: "Accounts Receivable Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "TOTAL RECEIVABLES", amount: "PKR 662,000.00", subtitle: "Outstanding", color: "blue", icon: <ProfitIcon /> },
        { label: "CURRENT (1-30 DAYS)", amount: "PKR 310,000.00", subtitle: "Within term", color: "green", icon: <BanknoteIcon /> },
        { label: "OVERDUE (>30 DAYS)", amount: "PKR 352,000.00", subtitle: "Action required", color: "red", icon: <CostsIcon /> },
        { label: "AGING RATIO", amount: "53.2%", subtitle: "Overdue %", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "T&T", message: "T&T: Accounts receivable has surpassed 90 days threshold. Payment collection is overdue." }
      ],
      tableTitle: "Customer Aging Analysis (open invoices)",
      tableColumns: [
        { name: "CUSTOMER", selector: (row) => row.customer, minWidth: "150px" },
        { name: "OUTSTANDING AMOUNT", selector: (row) => `PKR ${row.outstanding.toLocaleString()}`, minWidth: "180px" },
        { name: "1-30 DAYS", selector: (row) => `PKR ${row.days1_30.toLocaleString()}`, minWidth: "150px" },
        { name: "31-60 DAYS", selector: (row) => `PKR ${row.days31_60.toLocaleString()}`, minWidth: "150px" },
        { name: "61-90 DAYS", selector: (row) => `PKR ${row.days61_90.toLocaleString()}`, minWidth: "150px" },
        {
          name: "OVER 90 DAYS", selector: (row) => (
            <span style={{ color: row.daysOver90 > 0 ? "#B42318" : "#3D3F5C", fontWeight: row.daysOver90 > 0 ? 600 : 400 }}>
              PKR {row.daysOver90.toLocaleString()}
            </span>
          ), minWidth: "150px"
        },
        { name: "LAST INVOICED DATE", selector: (row) => row.lastInvoiced, minWidth: "180px" }
      ],
      tableData: [
        { customer: "Colgate", outstanding: 120000, days1_30: 80000, days31_60: 40000, days61_90: 0, daysOver90: 0, lastInvoiced: "2026-06-25" },
        { customer: "ICL", outstanding: 450000, days1_30: 150000, days31_60: 150000, days61_90: 150000, daysOver90: 0, lastInvoiced: "2026-06-20" },
        { customer: "Hamza Mughal", outstanding: 80000, days1_30: 80000, days31_60: 0, days61_90: 0, daysOver90: 0, lastInvoiced: "2026-06-28" },
        { customer: "T&T", outstanding: 120000, days1_30: 0, days31_60: 0, days61_90: 0, daysOver90: 120000, lastInvoiced: "2026-05-15" }
      ],
      tableNote: "Outstanding amounts include all approved, sent, and partially paid invoices. Drafted or cancelled invoices are excluded.",
      companySummary: {
        title: "Receivables Summary",
        rows: [
          { label: "Total Accounts Receivable Balance", value: "PKR 662,000.00", isGreen: true },
          { label: "- Current Receivables (1-30 Days)", value: "PKR 310,000.00" },
          { label: "- Overdue Receivables (31-90 Days)", value: "PKR 190,000.00", isIndented: true },
          { label: "- Critical Aging Receivables (>90 Days)", value: "PKR 120,000.00", isIndented: true, isBlue: true }
        ],
        note: "Receivables aging is calculated from the invoice issue date. Please follow up on balances older than 30 days."
      }
    };
  } else {
    return {
      type: "ap",
      typeLabel: "AP",
      title: "Accounts Payable Report",
      range: formattedRange,
      generatedInfo: `Generated by AJCL Admin on ${generatedTime}`,
      cards: [
        { label: "TOTAL PAYABLES", amount: "PKR 382,800.00", subtitle: "Accumulated payables", color: "red", icon: <CostsIcon /> },
        { label: "SUPPLIER BALANCE", amount: "PKR 232,800.00", subtitle: "PSO + Maintenance", color: "blue", icon: <ProfitIcon /> },
        { label: "PAYROLL BALANCE", amount: "PKR 150,000.00", subtitle: "Driver payouts", color: "green", icon: <BanknoteIcon /> },
        { label: "OVERDUE RATIO", amount: "2.0%", subtitle: "Due this week", color: "gray", icon: <MarginIcon /> }
      ],
      warnings: [
        { customer: "PSO Station", message: "PSO Station: fuel invoice of PKR 180,000.00 is due in 3 days. Prepare payout to avoid service disruption." }
      ],
      tableTitle: "Supplier & Vendor Payable Analysis",
      tableColumns: [
        { name: "SUPPLIER / VENDOR", selector: (row) => row.supplier, minWidth: "180px" },
        { name: "TOTAL PAYABLE", selector: (row) => `PKR ${row.outstanding.toLocaleString()}`, minWidth: "150px" },
        { name: "FUEL PURCHASE", selector: (row) => `PKR ${row.fuel.toLocaleString()}`, minWidth: "150px" },
        { name: "VEHICLE MAINTENANCE", selector: (row) => `PKR ${row.maintenance.toLocaleString()}`, minWidth: "180px" },
        { name: "DRIVER PAYOUTS", selector: (row) => `PKR ${row.payouts.toLocaleString()}`, minWidth: "150px" },
        { name: "DUE DATE", selector: (row) => row.dueDate, minWidth: "150px" }
      ],
      tableData: [
        { supplier: "PSO Station", outstanding: 180000, fuel: 180000, maintenance: 0, payouts: 0, dueDate: "2026-07-05" },
        { supplier: "Al Imran Spares", outstanding: 4500, fuel: 0, maintenance: 4500, payouts: 0, dueDate: "2026-07-10" },
        { supplier: "Driver Salaries", outstanding: 150000, fuel: 0, maintenance: 0, payouts: 150000, dueDate: "2026-07-01" },
        { supplier: "Shell Lubricants", outstanding: 7800, fuel: 0, maintenance: 7800, payouts: 0, dueDate: "2026-06-30" }
      ],
      tableNote: "Payables tracking includes unpaid fuel log purchases, locked maintenance items, and pending driver salaries.",
      companySummary: {
        title: "Payables Summary",
        rows: [
          { label: "Total Outstanding Accounts Payable", value: "PKR 382,800.00", isBlue: true },
          { label: "- Vendor & Supplier Liabilities", value: "PKR 232,800.00" },
          { label: "- Unpaid Driver Salaries", value: "PKR 150,000.00", isIndented: true, isGreen: true }
        ],
        note: "Vendor balances are updated from registered logs. Payout processing takes 2-3 business days."
      }
    };
  }
};

const resolveApiFileUrl = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  if (/^(https?:|blob:|data:)/i.test(normalizedValue)) return normalizedValue;
  return `${process.env.REACT_APP_API_FILES_URL || ""}${normalizedValue}`;
};

const FinanceReports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "pnl";
  const [loading, setLoading] = useState(true);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab); // pnl, ar, ap

  // Parameters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [reportFilterType, setReportFilterType] = useState("customer");
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const customerDropdownData = useSelector((state) => state.dropdowns?.customerDropdownData || []);
  const costCenterDropdownData = useSelector((state) => state.dropdowns?.costCenterDropdownData || []);

  // Reports list
  const defaultDummyHistory = useMemo(() => [
    {
      id: "fr-001",
      report_id: "FR-001",
      report_type: "Monthly Fleet PnL",
      created_at: "2026-06-30T18:00:00Z",
      created_by_name: "AJCL Admin",
      date_from: "2026-06-01",
      date_to: "2026-06-30",
      export_format: "PDF",
      total_revenue: 65850,
      total_expense: 382800,
      net_profit: -316950,
      profit_margin: -481.3
    },
    {
      id: "fr-002",
      report_id: "FR-002",
      report_type: "Monthly Fleet PnL",
      created_at: "2026-06-15T12:00:00Z",
      created_by_name: "AJCL Admin",
      date_from: "2026-05-01",
      date_to: "2026-05-31",
      export_format: "Excel",
      total_revenue: 1200000,
      total_expense: 950000,
      net_profit: 250000,
      profit_margin: 20.8
    },
    {
      id: "fr-003",
      report_id: "FR-003",
      report_type: "Accounts Receivable",
      created_at: "2026-06-28T10:00:00Z",
      created_by_name: "AJCL Admin",
      date_from: "2026-06-01",
      date_to: "2026-06-30",
      export_format: "CSV",
      total_revenue: 662000,
      total_expense: 310000,
      net_profit: 352000,
      profit_margin: 53.2
    },
    {
      id: "fr-004",
      report_id: "FR-004",
      report_type: "Accounts Payable",
      created_at: "2026-06-29T14:30:00Z",
      created_by_name: "AJCL Admin",
      date_from: "2026-06-01",
      date_to: "2026-06-30",
      export_format: "PDF",
      total_revenue: 382800,
      total_expense: 232800,
      net_profit: 150000,
      profit_margin: 2.0
    }
  ], []);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});

  const fetchHistory = async () => {
    if (activeTab === "ap") {
      setHistory([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }
    setLoadingHistory(true);
    try {
      let response;
      if (activeTab === "pnl") {
        response = await apiService.getFinancialPnlReportsPaginated({
          page: currentPage,
          limit: limit,
        });
      } else if (activeTab === "ar") {
        response = await apiService.getFinancialArReportsPaginated({
          page: currentPage,
          limit: limit,
        });
      }

      const pageData = response?.data || response || {};
      const results = pageData?.results || [];
      const meta = pageData?.meta || {};
      const total = typeof pageData?.total === 'number' ? pageData.total : (typeof meta?.totalItems === 'number' ? meta.totalItems : results.length);
      const pages = typeof meta?.totalPages === 'number' ? meta.totalPages : (Math.ceil(total / limit) || 1);

      setHistory(results);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (error) {
      errorNotify(error || "Failed to fetch financial reports history");
      setHistory([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchHistory().finally(() => setLoading(false));
  }, [activeTab, currentPage, limit]);

  useEffect(() => {
    if (!customerDropdownData || customerDropdownData.length === 0) {
      dispatch(fetchCustomerDropdown());
    }
    if (!costCenterDropdownData || costCenterDropdownData.length === 0) {
      dispatch(fetchCostCenterDropdown());
    }
  }, [dispatch, customerDropdownData, costCenterDropdownData]);

  // Map history rows exactly like buildFinancialReportHistoryRow in FinancialReports.jsx
  const mappedRows = useMemo(() => {
    return history.map((report) => {
      const reportId = report.report_id || report.financial_pnl_report_master_id || report.financial_receivable_master_id || report.financial_reports_id || (report.id !== undefined && report.id !== null ? `FR-${String(report.id).substring(0, 4).toUpperCase()}` : "-");
      const generatedOnValue = report.created_at || "";
      const generatedOnDisplay = generatedOnValue ? moment(generatedOnValue).format("YYYY-MM-DD hh:mm A") : "-";
      const startDate = report.date_from || report.start_date ? moment(report.date_from || report.start_date).format("YYYY-MM-DD") : "";
      const endDate = report.date_to || report.end_date ? moment(report.date_to || report.end_date).format("YYYY-MM-DD") : "";

      const creatorName = report.user?.username || report.user?.name || report.created_by_name || report.created_by_username || report.created_by_email || (report.created_by ? `User ${report.created_by}` : "-");
      const creatorImage = report.user?.image || report.user?.avatar || report.created_by_image || report.created_by_avatar || "";

      return {
        id: report.id,
        reportId,
        reportTypeKey: activeTab,
        reportType: activeTab === "pnl" ? "Profit & Loss" : activeTab === "ar" ? "Accounts Receivable" : "Accounts Payable",
        generatedOnValue,
        generatedOnDisplay,
        generatedBy: creatorName,
        generatedByImage: creatorImage,
        exportFormat: String(report.export_format || "pdf").toLowerCase(),
        startDate,
        endDate,
        dateRangeLabel: startDate && endDate ? `${startDate} to ${endDate}` : "-",
        totalRevenue: Number(report.total_revenue || report.details?.net_amount || 0),
        totalExpense: Number(report.total_expense || 0),
        netProfit: Number(report.net_profit || report.details?.outstanding_amount || 0),
        profitMargin: Number(report.profit_margin || 0),
        originalReport: report
      };
    });
  }, [history, activeTab]);

  // Filter mapped rows on the client-side using column filters
  const filteredMappedRows = useMemo(() => {
    return mappedRows.filter((row) => {
      return Object.entries(searchFilters || {}).every(([filterName, filterMeta]) => {
        const val = filterMeta?.value;
        if (val === undefined || val === null || val === "") return true;

        const propKey = filterName.replace("Filter", "");
        let rowVal = row[propKey];
        if (propKey === "generatedOn") rowVal = row.generatedOnValue;
        if (propKey === "generatedBy") rowVal = row.generatedBy;
        if (propKey === "dateRange") rowVal = row.dateRangeLabel;

        const rowString = String(rowVal || "").trim().toLowerCase();
        const filterString = String(val).trim().toLowerCase();

        return rowString.includes(filterString);
      });
    });
  }, [mappedRows, searchFilters]);

  // For server-side pagination, the returned rows are already paginated
  const paginatedRows = filteredMappedRows;

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      errorNotify("Please select Start Date and End Date.");
      return;
    }

    setGenerateLoading(true);

    try {
      let payload = {
        start_at: startDate,
        end_at: endDate,
      };

      let response;
      if (activeTab === "pnl") {
        response = await apiService.exportCustomerPnlPdf(payload);
        const filePath =
          response?.data?.file_path ||
          response?.data?.filePath ||
          response?.data?.url ||
          response?.data ||
          response?.file_path ||
          response?.filePath ||
          response?.url ||
          "";
        const fileUrl = resolveApiFileUrl(filePath);

        if (fileUrl) {
          const anchor = document.createElement("a");
          anchor.href = fileUrl;
          anchor.target = "_blank";
          anchor.download = `pnl_report_${startDate}_to_${endDate}.pdf`;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
      } else if (activeTab === "ar") {
        response = await apiService.exportAccountsReceivablePdf(payload);
        const filePath =
          response?.data?.file_path ||
          response?.data?.filePath ||
          response?.data?.url ||
          response?.data ||
          response?.file_path ||
          response?.filePath ||
          response?.url ||
          "";
        const fileUrl = resolveApiFileUrl(filePath);

        if (fileUrl) {
          const anchor = document.createElement("a");
          anchor.href = fileUrl;
          anchor.target = "_blank";
          anchor.download = `ar_report_${startDate}_to_${endDate}.pdf`;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
      }

      successNotify("Report generated successfully");

      // Fetch latest history to show the newly generated report in history
      await fetchHistory();

      // Reset parameter fields
      handleReset();
    } catch (error) {
      errorNotify(error || "Failed to generate report");
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setSelectedCustomer(null);
    setReportFilterType("customer");
    setSelectedCostCenter(null);
  };



  // Define table columns identical to the columns in FinancialReports.jsx
  const columns = useMemo(() => [
    {
      name: "Report ID",
      selector: (row) => (
        row.reportTypeKey === "pnl" || row.reportTypeKey === "ar" ? (
          <span>{row.reportId || "-"}</span>
        ) : (
          <button
            type="button"
            className="finance_link_button finance_link_text"
            onClick={() => navigate(`/finance-reports/details/${row.id}?type=${row.reportTypeKey}`)}
          >
            {row.reportId || "-"}
          </button>
        )
      ),
      tableColumn: true,
      filterName: "reportIdFilter",
      type: "text",
      minWidth: "140px",
    },
    {
      name: "Report Type",
      selector: (row) => renderFinancialReportTypeChip(row),
      tableColumn: true,
      filterName: "reportTypeFilter",
      type: "text",
      minWidth: "210px",
    },
    {
      name: "Generated On",
      selector: (row) => row.generatedOnDisplay || row.generatedOnValue || "-",
      tableColumn: true,
      filterName: "generatedOnFilter",
      type: "date",
      minWidth: "150px",
    },
    {
      name: "Created By",
      selector: (row) => (
        <div className="finance_created_by_cell">
          <UserAvatar
            username={row.generatedBy}
            image={row.generatedByImage}
            marginRight="0px"
          />
          <span className="finance_created_by_name">{row.generatedBy}</span>
        </div>
      ),
      tableColumn: true,
      filterName: "generatedByFilter",
      type: "text",
      minWidth: "200px",
    },
    {
      name: "Date Range",
      selector: (row) => (
        row.startDate && row.endDate ? (
          <div className="d-flex align-items-center gap-1 font-monospace" style={{ fontSize: "12px" }}>
            <span style={{ background: "#F1F5F9", color: "#334155", padding: "4px 8px", borderRadius: "6px", fontWeight: 500 }}>{row.startDate}</span>
            <span style={{ color: "#94A3B8", fontWeight: 600 }}>&rarr;</span>
            <span style={{ background: "#F1F5F9", color: "#334155", padding: "4px 8px", borderRadius: "6px", fontWeight: 500 }}>{row.endDate}</span>
          </div>
        ) : "-"
      ),
      tableColumn: true,
      filterName: "dateRangeFilter",
      type: "text",
      minWidth: "255px",
    },
    {
      name: "Export",
      selector: (row) => String(row.exportFormat || "-").toUpperCase(),
      tableColumn: true,
      filterName: "exportFormatFilter",
      type: "text",
      minWidth: "110px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="table_action_btn">
          {row.reportTypeKey === "pnl" || row.reportTypeKey === "ar" ? (
            <button
              type="button"
              className="edit_btn"
              title="Download PDF"
              onClick={() => {
                const filePath = row.originalReport?.file_path || "";
                const fileUrl = resolveApiFileUrl(filePath);
                if (fileUrl) {
                  window.open(fileUrl, "_blank");
                } else {
                  errorNotify("PDF URL not available");
                }
              }}
            >
              <FiDownload size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="edit_btn"
              title="Preview report"
              onClick={() => navigate(`/finance-reports/details/${row.id}?type=${row.reportTypeKey}`)}
            >
              <LuEye size={18} />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      width: "90px",
    }
  ], [activeTab, navigate]);

  const customerOptions = useMemo(() => {
    const dummyCustomers = [
      { id: "colgate", name: "Colgate" },
      { id: "icl", name: "ICL" },
      { id: "hamza-mughal", name: "Hamza Mughal" },
      { id: "tt", name: "T&T" }
    ];
    const sourceList = customerDropdownData.length > 0 ? customerDropdownData : dummyCustomers;
    return [
      { value: "all-customers", label: "All Customers" },
      ...sourceList.map((c) => ({
        value: c.id !== undefined && c.id !== null ? String(c.id) : String(c.value),
        label: c.name || c.label || `Customer #${c.id}`,
      })),
    ];
  }, [customerDropdownData]);

  const selectedCustomerOption = useMemo(() => {
    if (!selectedCustomer) return null;
    return customerOptions.find((o) => o.value === selectedCustomer) || null;
  }, [customerOptions, selectedCustomer]);

  const costCenterOptions = useMemo(() => {
    const dummyCostCenters = [
      { id: "1", name: "Karachi Depot" },
      { id: "2", name: "Lahore Fleet" }
    ];
    const sourceList = costCenterDropdownData.length > 0 ? costCenterDropdownData : dummyCostCenters;
    return sourceList.map((c) => ({
      value: c.id !== undefined && c.id !== null ? String(c.id) : String(c.value),
      label: c.name || c.label || `Cost Center #${c.id}`,
    }));
  }, [costCenterDropdownData]);

  const selectedCostCenterOption = useMemo(() => {
    if (!selectedCostCenter) return null;
    return costCenterOptions.find((o) => o.value === selectedCostCenter) || null;
  }, [costCenterOptions, selectedCostCenter]);

  const filterTypeOptions = useMemo(() => [
    { label: "Customer", value: "customer" },
  ], []);

  const selectedFilterTypeOption = useMemo(() => {
    if (!reportFilterType) return null;
    return filterTypeOptions.find((o) => o.value === reportFilterType) || null;
  }, [filterTypeOptions, reportFilterType]);



  return (
    <div className="trip_cost_estimation_page invoices_page">
      <Breadcrumbs values={["Finance", "Reports"]} />

      <div className="access_view" style={{ marginBottom: "24px" }}>
        <div>
          <h2>Reports</h2>
        </div>
      </div>

      {/* Report Cards Tabs */}
      <div className="financial_reports_type_grid mb-4" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <button
          type="button"
          className={`financial_reports_type_card ${activeTab === "pnl" ? "is_active" : ""}`}
          onClick={() => {
            setActiveTab("pnl");
            setCurrentPage(1);
            handleReset();
            navigate("/finance-reports?tab=pnl", { replace: true });
          }}
        >
          <strong>Profit & Loss (PNL)</strong>
          <p>Cash-basis revenue minus direct costs and locked driver salaries.</p>
        </button>

        <button
          type="button"
          className={`financial_reports_type_card ${activeTab === "ar" ? "is_active" : ""}`}
          onClick={() => {
            setActiveTab("ar");
            setCurrentPage(1);
            handleReset();
            navigate("/finance-reports?tab=ar", { replace: true });
          }}
        >
          <strong>Accounts Receivable (AR)</strong>
          <p>Issued invoices – payments – withheld, with aging buckets.</p>
        </button>

        <button
          type="button"
          className={`financial_reports_type_card ${activeTab === "ap" ? "is_active" : ""}`}
          onClick={() => {
            setActiveTab("ap");
            setCurrentPage(1);
            handleReset();
            navigate("/finance-reports?tab=ap", { replace: true });
          }}
        >
          <strong>Accounts Payable</strong>
          <p>Supplier, outsourced payroll, trip expenses and vendor payables.</p>
        </button>
      </div>

      {activeTab === "ap" ? (
        <div className="financial_reports_card text-center py-5 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "350px", border: "1px solid #ECECF2", borderRadius: "12px", background: "#ffffff" }}>
          <FiAlertTriangle size={54} className="text-warning mb-3" style={{ color: "#EAB308" }} />
          <h3 className="mb-2" style={{ color: "#3D3F5C", fontWeight: 600 }}>Accounts Payable Report</h3>
          <p className="text-muted" style={{ maxWidth: "450px", fontSize: "14px", lineHeight: "1.6" }}>
            This section is currently under construction. We are working to bring you detailed insights on supplier and vendor payables soon.
          </p>
        </div>
      ) : (
        <>
          {/* Report Parameters Box */}
          <div className="financial_reports_card mb-4">
            <div className="financial_reports_card_header">
              <h3>Report Parameters</h3>
              <p>Data sourced from Expense Ledger, Fuel, Driver Expenses, Maintenance and Inventory</p>
            </div>

            <Row className="financial_reports_fields_row align-items-end">
              {activeTab === "pnl" && (
                <Col md={(!reportFilterType || reportFilterType === "all") ? 4 : 3} className="mb-3 mb-md-0">
                  <Select
                    key={reportFilterType || "empty"}
                    label="Source Type"
                    placeholder="Select Source Type"
                    options={filterTypeOptions}
                    value={selectedFilterTypeOption}
                    onChange={(option) => {
                      setReportFilterType(option?.value || null);
                      setSelectedCustomer(null);
                      setSelectedCostCenter(null);
                    }}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                  />
                </Col>
              )}
              <Col md={activeTab === "pnl" ? ((!reportFilterType || reportFilterType === "all") ? 4 : 3) : 4} className="mb-3 mb-md-0">
                <Input
                  type="date"
                  label="Start Date"
                  name="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col md={activeTab === "pnl" ? ((!reportFilterType || reportFilterType === "all") ? 4 : 3) : 4} className="mb-3 mb-md-0">
                <Input
                  type="date"
                  label="End Date"
                  name="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              {activeTab === "pnl" ? (
                /* (reportFilterType && reportFilterType !== "all") && (
                  <Col md={3} className="mb-3 mb-md-0">
                    {reportFilterType === "customer" ? (
                      <Select
                        key={selectedCustomer || "pnl-cust-empty"}
                        label="Customer (PNL / optional)"
                        placeholder="Select Customer"
                        options={customerOptions}
                        value={selectedCustomerOption}
                        onChange={(option) => setSelectedCustomer(option?.value || null)}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                      />
                    ) : (
                      <Select
                        key={selectedCostCenter || "pnl-cc-empty"}
                        label="Cost Center"
                        placeholder="Select Cost Center"
                        options={costCenterOptions}
                        value={selectedCostCenterOption}
                        onChange={(option) => setSelectedCostCenter(option?.value || null)}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                      />
                    )}
                  </Col>
                ) */ null
              ) : (
                /* <Col md={4} className="mb-3 mb-md-0">
                  <Select
                    key={selectedCustomer || "ar-cust-empty"}
                    label={`Customer (${activeTab.toUpperCase()} / optional)`}
                    placeholder="Select Customer"
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={(option) => setSelectedCustomer(option?.value || null)}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                  />
                </Col> */ null
              )}
            </Row>

            <div className="financial_reports_actions" style={{ marginTop: "24px" }}>
              <Button
                shouldRow={false}
                type="button"
                submitText="Generate Report"
                loadingText="Generating Report..."
                submitBtn={handleGenerateReport}
                cancelBtn={handleReset}
                cancelBtnText="Reset"
                loading={generateLoading}
                disabled={
                  !startDate ||
                  !endDate ||
                  generateLoading
                }
                cancelBtnDisabled={
                  (!startDate &&
                    !endDate) ||
                  generateLoading
                }
                isShowSubmitBtn={true}
                isShowCancel={true}
              />
            </div>
          </div>

          {/* Report History (matching Financial Report History table layout) */}
          <div className="financial_reports_card" style={{ paddingLeft: "0px", paddingRight: "0px" }}>
            <div className="financial_reports_card_header" style={{ paddingLeft: "24px", paddingRight: "24px" }}>
              <h3>Financial Report History</h3>
            </div>

            <NewTableComponent
              showAddBtn={false}
              columns={columns}
              data={paginatedRows}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              limit={limit}
              setLimit={setLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              loading={loadingHistory}
              paginationLoading={loadingHistory}
              limitOptions={[10, 20, 50, 100]}
              paginationClassName="inline_pagination"
              tableClassName="invoices_table parts_table_fix financial_reports_table"
              searchFilters={searchFilters}
              setSearchFilters={setSearchFilters}
              showFilter={false}
            />
          </div>
        </>
      )}

    </div>
  );
};

export default FinanceReports;
